import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AccessCodeEntry } from '../../components/user/AccessCodeEntry';
import { 
  Shield, 
  BookOpen, 
  Users,
  Award,
  ArrowRight,
  Building,
  CheckCircle
} from 'lucide-react';
import { AccessCode } from '../../types/accessCodes';

export const TrainingAccess: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userHasAccess, setUserHasAccess] = useState<boolean | null>(null);
  const [userAccessCode, setUserAccessCode] = useState<AccessCode | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUserAccess();
  }, [user]);

  const checkUserAccess = async () => {
    if (!user) return;

    try {
      // Check if user already has an access code
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.accessCodeId) {
          // User has access code, verify it's still valid
          const accessCodeDoc = await getDoc(doc(db, 'accessCodes', userData.accessCodeId));
          if (accessCodeDoc.exists()) {
            const accessCodeData = {
              id: accessCodeDoc.id,
              ...accessCodeDoc.data(),
              createdAt: accessCodeDoc.data().createdAt?.toDate(),
              updatedAt: accessCodeDoc.data().updatedAt?.toDate(),
              expiresAt: accessCodeDoc.data().expiresAt?.toDate(),
              lastUsedAt: accessCodeDoc.data().lastUsedAt?.toDate(),
            } as AccessCode;

            if (accessCodeData.isActive && 
                (!accessCodeData.expiresAt || accessCodeData.expiresAt > new Date())) {
              setUserHasAccess(true);
              setUserAccessCode(accessCodeData);
              // Auto-redirect to training after a moment
              setTimeout(() => navigate('/training'), 2000);
              return;
            }
          }
        }
      }

      setUserHasAccess(false);
    } catch (error) {
      console.error('Error checking user access:', error);
      setUserHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAccessCodeSuccess = (accessCode: AccessCode) => {
    setUserHasAccess(true);
    setUserAccessCode(accessCode);
    // Redirect to training after success
    setTimeout(() => navigate('/training'), 1500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Checking training access...</p>
        </div>
      </div>
    );
  }

  if (userHasAccess && userAccessCode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 max-w-lg w-full text-center">
          <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-float">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Welcome to Training!
          </h1>
          
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-center space-x-3">
              <Building className="w-6 h-6 text-blue-600" />
              <div className="text-left">
                <div className="font-semibold text-blue-900">{userAccessCode.departmentName}</div>
                <div className="text-blue-700 text-sm">Access verified</div>
              </div>
            </div>
          </div>

          <p className="text-gray-600 mb-6">
            You now have access to all training modules. Your progress will be tracked for department analytics.
          </p>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/training')}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
            >
              <BookOpen className="w-5 h-5" />
              <span>Start Training</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Training Access Required</h1>
          <p className="text-gray-600 text-lg">
            To access our specialized first responder training modules, you'll need a department access code.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Access Code Entry */}
          <div>
            <AccessCodeEntry 
              onSuccess={handleAccessCodeSuccess}
              required={true}
            />
          </div>

          {/* Benefits */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">What You'll Get Access To:</h2>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">20+ Training Modules</h3>
                  <p className="text-gray-600 text-sm">Evidence-based wellness training designed specifically for first responders</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Department Analytics</h3>
                  <p className="text-gray-600 text-sm">Your department gets detailed progress reports and usage analytics</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                  <Award className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Progress Tracking</h3>
                  <p className="text-gray-600 text-sm">Track your learning journey and share achievements with your team</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-orange-100 p-2 rounded-lg flex-shrink-0">
                  <Shield className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Secure & Private</h3>
                  <p className="text-gray-600 text-sm">Your personal wellness data remains private while department gets aggregate insights</p>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Need an access code?</h3>
              <p className="text-sm text-gray-600">
                Contact your department's training coordinator, wellness officer, or administrative staff to obtain your access code.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
